import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ActiveClientProptype } from './prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import ModalPortal from '../../../components/modal-portal';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { Button } from 'ui-components/components/button';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_INFO,
} from 'ui-components/services/notification-service';

import { refreshStoreList } from '../../../store/seller/seller.actions';
import { deactivateClient as deactivateClientAsync } from '../api/fulfillment-delegation';

import messages from './messages';
import style from './style.module.less';

const errorNotification = {
  title: '',
  notificationType: NOTIFICATION_TYPE_ERROR,
  content: <FormattedMessage {...messages.deactivateClientError} />,
  duration: NOTIFICATION_DURATIONS.SHORT,
};

export const DeactivateClientModal = ({ client, onClose, reloadGrid }) => {
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const { clientName, externalClientConnectionId } = client;
  const dispatch = useDispatch();

  const updateNotification = (notificationId, notificationType, title, content) => {
    NotificationService.updateNotification(notificationId, {
      title,
      content,
      notificationType,
      progress: false,
      dontHide: false,
    });
  };

  const deactivateClient = useCallback(async () => {
    setIsLoading(true);
    const notificationId = NotificationService.showNotification({
      title: <FormattedMessage {...messages.deactivatingClientTitle} />,
      content: <FormattedMessage {...messages.deactivatingClientMessage} values={{ clientName }} />,
      notificationType: NOTIFICATION_TYPE_INFO,
      progress: true,
      dontHide: true,
    });
    try {
      await deactivateClientAsync(externalClientConnectionId);
      onClose();
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_SUCCESS,
        <FormattedMessage {...messages.succesfulDeactivationTitle} />,
        <FormattedMessage {...messages.succesfulDeactivationMessage} values={{ clientName }} />,
      );
      reloadGrid();
      dispatch(refreshStoreList());
    } catch {
      NotificationService.showNotification(errorNotification);
      setIsLoading(false);
      onClose();
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_ERROR,
        <FormattedMessage {...messages.failedDeactivationTitle} />,
        <FormattedMessage {...messages.failedDeactivationMessage} />,
      );
    }
  }, [clientName, dispatch, externalClientConnectionId, onClose, reloadGrid, setIsLoading]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.deactivateClientModalHeader} values={{ clientName }} />
        </ModalHeader>
        <ModalBody>
          <div className={style['deactivate-client-modal-body']}>
            <FormattedMessage {...messages.deactivateClientModalBody} />
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton />
          <Button disabled={isLoading} onClick={deactivateClient} theme='danger'>
            <FormattedMessage {...messages.deactivateClientButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

DeactivateClientModal.propTypes = {
  client: ActiveClientProptype.isRequired,
  onClose: PropTypes.func.isRequired,
  reloadGrid: PropTypes.func.isRequired,
};
